import { Box, IconButton } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import { ReactComponent as Hamburger } from "assets/Hamburger.svg";
import ThemeContext from "components/app/Context";
import BackButton from "components/common/BackButton";
import Notifications from "components/features/Notifications";
import { isEmpty } from "lodash";
import { useContext, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import {
  ABOVE_PROP_USER_TYPE,
  DEFAULT_CORP_INNCODE,
  RETRIEVE_NOTIFICATION_EVENT_TYPE,
  abovePropUserView,
  abovePropertyViewTypePlanTemplate,
  missingIncodeText
} from "utils/CommonConstants";
import { decrypt, getHltInncodes, getIsCorpUser, setInitialWizardTaken } from "utils/CommonFunctions";
import Logo from "../Logo";
import UserMenu from "../UserMenu";

import { fetchRoles, setAbovePropertyUser, setAdmin, setInnCode } from "components/app/App.slice";
import { fetchFilterCategories } from "components/common/Filters/Filters.slice";
import { fetchBookmarkPlan, fetchPropertyInfoAsync } from "components/features/AboveProperty/AboveProperty.slice";
import AskBoost from "components/features/AskBoost";
import { setInncodeDetails } from "components/features/MyPlans/myplans.slice";
import { getNotifications } from "components/features/Notifications/Notifications.slice";
import { setPropertyList } from "components/features/PlanAssignment/PlanAssignment.slice";
import { fetchBookmarkedInncodeAsync } from "components/features/dashboard/Dashboard.slice";
import { fetchBookmarkPlansIdAsync } from "components/features/library/Bookmark/Bookmark.slice";
import { nestedTernaryCheckerCond1 } from "utils/CommonChecker";
import Pendo from "../Pendo";
import "./index.scss";

interface Props {
  toggleDrawer: any;
}

function TopNavbar(props: Props) {
  const dispatch = useAppDispatch();
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const { isMobileScreen } = useContext(ThemeContext);
  const innCodeData = useAppSelector((state: any) => state.app.innCode);
  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);
  const { toggleDrawer } = props;
  const innCode: any = localStorage.getItem("defaultInncode");
  const innCodeValue = localStorage.getItem("innCode") ? localStorage.getItem("innCode") : innCode;
  const lobbyUserType = decrypt("salt", localStorage.getItem("lobbyUserType"));
  const isAboveProperty = useAppSelector((state) => state.app.loggedInUserDetails.isAbovePropertyUser);
  const isAbovePropertyUserFetched: any = useAppSelector(
    (state) => state.app.loggedInUserDetails.isAbovePropertyUserFetched
  );
  const isAbovePropertyUser = isAbovePropertyUserFetched ? isAboveProperty : true;
  const innCodesData = useAppSelector((state: any) => state.app.innCodes);
  const adminInncodes = useAppSelector((state) => state.myplans.MyPlans.adminInncodes);
  const bookmarkedIdList = useAppSelector((state) => state.aboveProperty?.bookmarkedIdList);
  const bookmarkedTemplate = useAppSelector((state) => state.aboveProperty?.bookmarkedTemplate);
  const viewAs = useAppSelector((state) => state.app.viewAsForAbovePropUser);

  useEffect(() => {
    if (isAbovePropertyUserFetched) {
      const innCodesValue = getHltInncodes(true);
      fetchPropInfo(innCodesValue);
      dispatch(fetchBookmarkPlansIdAsync());
      if (isEmpty(bookmarkedIdList) && isEmpty(bookmarkedTemplate) && isAbovePropertyUser)
        dispatch(fetchBookmarkPlan({ viewType: abovePropertyViewTypePlanTemplate }));
      dispatch(
        getNotifications({
          size: 30,
          eventType: RETRIEVE_NOTIFICATION_EVENT_TYPE
        })
      );
    }
  }, [isImpersonated, isAbovePropertyUser, isAbovePropertyUserFetched]);

  const fetchPropInfo = async (innCodesValue: any) => {
    const res = await dispatch(fetchPropertyInfoAsync({ inncodes: innCodesValue }));
    dispatch(setPropertyList(Object.values(res?.payload)));
  };

  useEffect(() => {
    dispatch(fetchFilterCategories({}));
  }, [isImpersonated]);

  useEffect(() => {
    const isAbovePropertyUser = String(lobbyUserType).toLowerCase() === ABOVE_PROP_USER_TYPE;
    if (!isImpersonated) {
      if (viewAs === abovePropUserView["AP"]) dispatch(setAbovePropertyUser(isAbovePropertyUser));
      dispatch(fetchRoles({}));
      //if normal user then it will always be true
      isAbovePropertyUser && setInitialWizardTaken();
    }

    if (isCorp) {
      dispatch(setInnCode(DEFAULT_CORP_INNCODE));
    } else {
      if (innCodeValue !== missingIncodeText) dispatch(setInnCode(innCodeValue));
    }
  }, [isImpersonated, isCorp]);

  useEffect(() => {
    if (!isImpersonated) {
      if (getIsCorpUser(lobbyUserType)) {
        dispatch(setAdmin(true));
        dispatch(fetchBookmarkedInncodeAsync({}));
        setInitialWizardTaken(); //Set always true if corp user
      } else {
        dispatch(setAdmin(false));
      }
    }
  }, [lobbyUserType, isImpersonated]);

  useEffect(() => {
    if (isCorp) {
      let tempInncodes: any = innCodesData.length ? innCodesData : adminInncodes;
      tempInncodes = tempInncodes.filter((item: any) => item.innCode === innCodeData);
      if (tempInncodes?.length) dispatch(setInncodeDetails(tempInncodes));
    }
  }, [innCodeData]);

  return (
    <AppBar
      elevation={0}
      className="app-bar"
      sx={{
        top: nestedTernaryCheckerCond1(isImpersonated, isMobileScreen, "117px", "50px", "0"),
        paddingX: "32px",
        minHeight: "76px",
        width: "100%",
        paddingLeft: isMobileScreen ? "32px" : "130px",
        justifyContent: "space-between",
        borderBottom: isMobileScreen ? `1px solid ${tokens.Border.General.Secondary}` : "none"
      }}
    >
      <Pendo />
      {isMobileScreen ? (
        <>
          <IconButton sx={{ paddingX: 0 }} onClick={toggleDrawer(true)}>
            <Hamburger />
          </IconButton>
          <Logo />
          <Box>
            <Notifications />
          </Box>
        </>
      ) : (
        <div className="desktop-container">
          <div className="back-button">
            <BackButton label="Back" />
          </div>
          <div className="ask-boost-container">
            <AskBoost />
          </div>
          <div className="vertical-center">
            <UserMenu toggleDrawer={toggleDrawer} />
          </div>
        </div>
      )}
    </AppBar>
  );
}
export default TopNavbar;
